import { action, Action, computed, Computed, thunk, Thunk } from 'easy-peasy';
import axios from 'axios';
import {
    CommunicationChatDetails,
    CommunicationChatRecipient,
    Message,
    OpenCommunicationChatPayload,
    SaveChatUserPayload,
    SaveChatUserResponse,
    SearchUser,
    IssueChatPayload,
} from '~/types';
import { StoreModel } from '~/store';
import { EMPTY_COMMUNICATION_CHAT_DATA } from '~/app/data/statuses/chat';

let openGeneration = 0;

export type PrepareCommunicationChatPayload = {
    machineId: string | number
    projectId: string | number
    chatId?: number
    roleId?: number
    userIds?: number[]
    recipients?: CommunicationChatRecipient[]
    title?: string
    roleName?: string
};

const normalizePayload = (payload: OpenCommunicationChatPayload): OpenCommunicationChatPayload => ({
    machineId: Number(payload.machineId),
    projectId: Number(payload.projectId),
    roleId: Number(payload.roleId),
    userIds: (payload.userIds ?? []).map(id => Number(id)).filter(id => id > 0),
    recipients: payload.recipients,
});

const buildLocalChatUsers = (
    ownerId: number | string,
    ownerName: string,
    userIds: number[],
    recipients?: CommunicationChatRecipient[],
): SearchUser[] => {
    const names = new Map((recipients ?? []).map(r => [Number(r.id), r.name]));
    const users: SearchUser[] = [{
        id: String(ownerId),
        name: ownerName || '',
        email: '',
        phone: '',
        avatar: '',
        added: true,
        deleted: false,
        new: false,
    }];
    userIds.forEach(id => {
        const uid = Number(id);
        if (uid > 0 && uid !== Number(ownerId)) {
            users.push({
                id: String(uid),
                name: names.get(uid) || `User ${uid}`,
                email: '',
                phone: '',
                avatar: '',
                added: true,
                deleted: false,
                new: false,
            });
        }
    });
    return users;
};

export interface CommunicationChatModel {
    data: CommunicationChatDetails
    fetching: boolean
    setFetching: Action<CommunicationChatModel, boolean>
    prepareOpenChat: Thunk<CommunicationChatModel, PrepareCommunicationChatPayload, any, StoreModel, Promise<void>>
    openChat: Thunk<CommunicationChatModel, OpenCommunicationChatPayload, any, StoreModel, Promise<void>>
    openChatById: Thunk<CommunicationChatModel, number, any, StoreModel, Promise<void>>
    startMachineChat: Thunk<CommunicationChatModel, OpenCommunicationChatPayload | void, any, StoreModel, Promise<number>>
    setChatData: Action<CommunicationChatModel, CommunicationChatDetails>
    tempChatUsers: SearchUser[]
    setTempChatUsers: Action<CommunicationChatModel, SearchUser[]>
    setChatUsers: Action<CommunicationChatModel, SearchUser[]>
    fetchChatUsers: Thunk<CommunicationChatModel, CommunicationChatDetails['id'], any, StoreModel, Promise<void>>
    saveChatUser: Thunk<CommunicationChatModel, SaveChatUserPayload, any, StoreModel, Promise<SaveChatUserResponse>>
    addChatUser: Thunk<CommunicationChatModel, IssueChatPayload, any, StoreModel, Promise<SaveChatUserResponse>>
    addChatMessage: Action<CommunicationChatModel, Message>
    isReadOnlyChat: Computed<CommunicationChatModel, boolean>
    chatUserUpdating: boolean
    setChatUserUpdating: Action<CommunicationChatModel, boolean>
    reset: Action<CommunicationChatModel>
    cancelPendingOpen: Action<CommunicationChatModel>
}

export const communicationChatModel: CommunicationChatModel = {
    data: { ...EMPTY_COMMUNICATION_CHAT_DATA },
    fetching: false,
    tempChatUsers: [],
    chatUserUpdating: false,

    prepareOpenChat: thunk(async (actions, payload, { getStoreState }) => {
        actions.setChatData({
            ...EMPTY_COMMUNICATION_CHAT_DATA,
            id: payload.chatId ?? '',
            machineId: payload.machineId,
            projectId: payload.projectId,
            roleId: payload.roleId,
            recipientUserIds: payload.userIds,
            title: payload.title,
            roleName: payload.roleName,
            recipients: payload.recipients,
        });

    }),
    setFetching: action((state, payload) => {
        state.fetching = payload;
    }),
    setChatUserUpdating: action((state, payload) => {
        state.chatUserUpdating = payload;
    }),
    setTempChatUsers: action((state, payload) => {
        state.tempChatUsers = payload;
    }),
    setChatUsers: action((state, payload) => {
        state.data.chatUsers = payload;
        state.tempChatUsers = payload;
    }),
    setChatData: action((state, payload) => {
        state.data = { ...EMPTY_COMMUNICATION_CHAT_DATA, ...payload };
    }),
    reset: action((state) => {
        state.data = { ...EMPTY_COMMUNICATION_CHAT_DATA };
        state.tempChatUsers = [];
        state.fetching = false;
    }),
    cancelPendingOpen: action(() => {
        openGeneration += 1;
    }),

    /** Open chat for machine + role + selected user(s). API uses MachineChat + PersonnelChat. */
    openChat: thunk(async (actions, payload, { getStoreState }) => {
        const generation = ++openGeneration;
        const request = normalizePayload(payload);
        const current = getStoreState().communicationChat?.data ?? EMPTY_COMMUNICATION_CHAT_DATA;
        const user = getStoreState().user?.data;
        const ownerId = user?.id ?? 0;
        const ownerName = user?.name ?? '';
        const localUsers = buildLocalChatUsers(ownerId, ownerName, request.userIds, request.recipients);

        actions.setChatData({
            ...EMPTY_COMMUNICATION_CHAT_DATA,
            id: 0,
            machineId: request.machineId,
            projectId: request.projectId,
            roleId: request.roleId,
            recipientUserIds: request.userIds,
            recipients: request.recipients ?? current.recipients,
            title: current.title,
            roleName: current.roleName,
            chat: [],
            chatUsers: localUsers,
        });
        actions.setFetching(true);

        try {
            const response = await axios.post<null, {
                chatId?: number
                machineId?: number
                projectId?: number
                machineIntern?: string
                machineName?: string
                categoryName?: string
                categoryImage?: string
                roleName?: string
                projectNumber?: string
                projectName?: string
                chat?: Message[]
                chatUsers?: SearchUser[]
            }>('/machines/chat/open', request);

            if (generation !== openGeneration) {
                return;
            }

            actions.setChatData({
                id: Number(response?.chatId) || 0,
                machineId: response?.machineId ?? request.machineId,
                projectId: response?.projectId ?? request.projectId,
                machineIntern: response?.machineIntern ?? '',
                machineName: response?.machineName ?? '',
                categoryName: response?.categoryName ?? '',
                categoryImage: response?.categoryImage ?? '',
                projectNumber: response?.projectNumber,
                projectName: response?.projectName,
                roleId: request.roleId,
                recipientUserIds: request.userIds,
                recipients: request.recipients ?? current.recipients,
                title: current.title,
                roleName: response?.roleName ?? current.roleName,
                chat: response?.chat ?? [],
                chatUsers: response?.chatUsers?.length ? response.chatUsers : localUsers,
            });
        } catch {
            if (generation !== openGeneration) {
                return;
            }
            actions.setChatData({
                ...EMPTY_COMMUNICATION_CHAT_DATA,
                id: 0,
                machineId: request.machineId,
                projectId: request.projectId,
                roleId: request.roleId,
                recipientUserIds: request.userIds,
                recipients: request.recipients ?? current.recipients,
                title: current.title,
                roleName: current.roleName,
                chat: [],
                chatUsers: localUsers,
            });
        } finally {
            if (generation === openGeneration) {
                actions.setFetching(false);
            }
        }
    }),

    openChatById: thunk(async (actions, chatId, { getStoreState }) => {
        const generation = ++openGeneration;
        const current = getStoreState().communicationChat?.data ?? EMPTY_COMMUNICATION_CHAT_DATA;
        actions.setFetching(true);
        try {
            const response = await axios.post<null, {
                chatId?: number
                machineId?: number
                projectId?: number
                machineIntern?: string
                machineName?: string
                categoryName?: string
                categoryImage?: string
                roleId?: number
                roleName?: string
                chat?: Message[]
                chatUsers?: SearchUser[]
            }>('/machines/chat/open', { chatId });

            if (generation !== openGeneration) return;

            const chatUsers = response?.chatUsers ?? [];
            const recipientUserIds = current.recipientUserIds?.length
                ? current.recipientUserIds
                : chatUsers.map(u => Number(u.id)).filter(id => id > 0);

            actions.setChatData({
                id: Number(response?.chatId) || chatId,
                machineId: response?.machineId ?? current.machineId ?? 0,
                projectId: response?.projectId ?? current.projectId ?? 0,
                machineIntern: response?.machineIntern ?? '',
                machineName: response?.machineName ?? '',
                categoryName: response?.categoryName ?? '',
                categoryImage: response?.categoryImage ?? '',
                roleId: response?.roleId ?? current.roleId ?? 0,
                recipientUserIds,
                recipients: current.recipients,
                title: current.title,
                roleName: response?.roleName ?? current.roleName ?? '',
                chat: response?.chat ?? [],
                chatUsers,
            });
        } catch {
            if (generation !== openGeneration) return;
            actions.setChatData({
                ...EMPTY_COMMUNICATION_CHAT_DATA,
                id: chatId,
                machineId: current.machineId,
                projectId: current.projectId,
                roleId: current.roleId,
                recipientUserIds: current.recipientUserIds,
                recipients: current.recipients,
                title: current.title,
                roleName: current.roleName,
            });
        } finally {
            if (generation === openGeneration) {
                actions.setFetching(false);
            }
        }
    }),

    /** Create MachineChat row + PersonnelChat on first message. */
    startMachineChat: thunk(async (actions, payload, { getStoreState }) => {
        const chat = getStoreState().communicationChat?.data ?? EMPTY_COMMUNICATION_CHAT_DATA;
        const user = getStoreState().user?.data;
        const request = normalizePayload({
            machineId: payload?.machineId ?? chat.machineId,
            projectId: payload?.projectId ?? chat.projectId,
            roleId: payload?.roleId ?? chat.roleId ?? 0,
            userIds: payload?.userIds ?? chat.recipientUserIds ?? [],
            recipients: payload?.recipients,
        });

        if (!request.machineId || !request.projectId || !request.roleId || !request.userIds.length) {
            throw new Error('Machine chat session is incomplete');
        }

        const response = await axios.post<null, {
            chatId?: number
            chatUsers?: SearchUser[]
            msg?: string
        }>('/machines/chat/start', request);

        const chatId = Number(response?.chatId);
        if (!chatId) {
            throw new Error(response?.msg || 'Could not start machine chat');
        }

        const localUsers = buildLocalChatUsers(
            user?.id ?? 0,
            user?.name ?? '',
            request.userIds,
            request.recipients,
        );

        actions.setChatData({
            ...chat,
            id: chatId,
            machineId: request.machineId,
            projectId: request.projectId,
            roleId: request.roleId,
            recipientUserIds: request.userIds,
            chatUsers: response?.chatUsers?.length ? response.chatUsers : localUsers,
        });

        return chatId;
    }),

    fetchChatUsers: thunk((actions, chatId) => (
        axios
            .post<null, SearchUser[]>(`/machines/chat/users/fetch/${chatId}`)
            .then(users => {
                actions.setChatUsers(Array.isArray(users) ? users : []);
            })
            .catch(() => {})
    )),

    saveChatUser: thunk((actions, payload) => {
        actions.setChatUserUpdating(true);
        return axios
            .post<null, SaveChatUserResponse>('/machines/chat/users/save', payload)
            .finally(() => actions.setChatUserUpdating(false));
    }),

    addChatUser: thunk((actions, payload) => (
        axios.post<null, SaveChatUserResponse>('/machines/chat/users/add', payload)
    )),

    isReadOnlyChat: computed(() => false),

    addChatMessage: action((state, payload) => {
        if (Number(state.data.id) !== Number(payload.issueId)) {
            return;
        }
        const normalize = (text: string) => (text || '').replace(/<[^>]+>/g, '').trim();
        const normalizeUrls = (urls?: string[]) => (urls ?? []).join('|');
        const exists = state.data.chat.some(
            m => String(m.senderId) === String(payload.senderId)
                && normalize(m.message) === normalize(payload.message)
                && normalizeUrls(m.attachmentUrls) === normalizeUrls(payload.attachmentUrls),
        );
        if (!exists) {
            state.data.chat.push(payload);
        }
    }),
};
